"use client"

import { AlertCircle, RefreshCw } from "lucide-react"
import { Button } from "./button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./card"

interface ErrorDisplayProps {
  error: string | Error | null
  title?: string
  description?: string
  onRetry?: () => void
  retryLabel?: string
}

/**
 * Standardized error display component
 * Provides consistent error UI across the application
 */
export function ErrorDisplay({
  error,
  title = "Something went wrong",
  description,
  onRetry,
  retryLabel = "Try Again",
}: ErrorDisplayProps) {
  const errorMessage = error instanceof Error ? error.message : error || "An unknown error occurred"

  return (
    <Card className="bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-red-800 dark:text-red-200">
          <AlertCircle className="w-5 h-5" />
          {title}
        </CardTitle>
        {description && <CardDescription className="text-red-700 dark:text-red-300">{description}</CardDescription>}
      </CardHeader>
      <CardContent>
        <p className="text-sm text-red-700 dark:text-red-300 mb-4">{errorMessage}</p>
        {onRetry && (
          <Button onClick={onRetry} variant="outline" className="border-red-300 text-red-700 hover:bg-red-100 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900/40">
            <RefreshCw className="w-4 h-4 mr-2" />
            {retryLabel}
          </Button>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * Inline error display for smaller errors (e.g., within cards)
 */
export function InlineError({ error, onRetry }: { error: string | null; onRetry?: () => void }) {
  if (!error) return null

  return (
    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
      <div className="flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          {onRetry && (
            <Button
              onClick={onRetry}
              variant="ghost"
              size="sm"
              className="mt-2 text-red-700 hover:text-red-800 hover:bg-red-100 dark:text-red-300 dark:hover:bg-red-900/40"
            >
              <RefreshCw className="w-3 h-3 mr-1" />
              Retry
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}




